import { useParams, useNavigate } from "react-router-dom";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { useRequirementsStore } from "@/frontend/store/RequirementsStore";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { useShallow } from "zustand/react/shallow";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { I18n } from "@/i18n/I18n";
import { ProjectRequirementCard } from "./ProjectRequirementCard";
import { ProjectReadyTaskCard } from "./ProjectReadyTaskList";
import { TruncatedTextWithModal } from "../cards/TruncatedTextWithModal";
import { NotFound } from "@/frontend/pages/NotFound";
import { Project, ProjectParentType } from "@/backend/models/project";
import { FusawarePermission } from "@/frontend/fusawareUser";
import { CreateProjectFromTemplateModal } from "./CreateProjectFromTemplateModal";
import { RequirementSubjectType } from "@/backend/access/requirement/types";

export function ProjectPage() {
  const projectId = useParams().projectId;
  const project = useProjectStore(useShallow(state => state.projects.find(project => project.id === projectId)));
  const assignedRequirements = useRequirementsStore(useShallow(state => state.requirements
    .filter(requirement => requirement.subjectId === projectId && requirement.subjectType === RequirementSubjectType.PROJECT)
    .sort((a) => a.applicable ? -1 : 1)
  ));
  const fetchProjectRequirements = useRequirementsStore(state => state.fetchRequirementsBySubject);
  const recordProjectView = useProjectStore(state => state.recordProjectView);
  const userOrgId = useGlobalStore(state => state.userClass?.getOrgId());
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if(projectId) {
      setLoading(true);
      void fetchProjectRequirements(projectId, RequirementSubjectType.PROJECT).finally(() => {
        setLoading(false);
      });
      void recordProjectView(projectId);
    }
  }, [fetchProjectRequirements, projectId, recordProjectView]);

  const displayedRequirements = assignedRequirements?.slice(0, 5) ?? [];
  const hasMoreRequirements = (assignedRequirements?.length ?? 0) > 5;

  if(!project) {
    return <NotFound />;
  }

  return (
    <div className="p-3">
      <div className="pb-3">
        <h4 className="card-title">{project.title}</h4>
        {project.description && (
          <TruncatedTextWithModal 
            text={project.description} 
            maxLength={300}
            className="text-muted"
            modalTitle={project.title}
          />
        )}
        {project.parentType === ProjectParentType.ORGANIZATION && (
          <CreateProjectFromTemplateButton templateProject={project} />
        )}
      </div>
      
      <div className="d-flex gap-3 align-items-start">
        <div className="list-card" style={{ width: '520px' }}>
          <div className="list-card-title">{I18n.get('requirements')}</div>
          { loading ? (
              <div className="text-center py-4">
                <p className="text-muted">{I18n.get('loading')}</p>
              </div>
            ) : displayedRequirements.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">{I18n.get('noRequirementsAssigned')}</p>
            </div>
          ) : (
            <div className='list-card-block'>
              {displayedRequirements.map((requirement) => (
                <ProjectRequirementCard
                  key={requirement.id}
                  requirement={requirement}
                />
              ))}
              { hasMoreRequirements && <div className="d-flex justify-content-center" style={{ padding: '8px 12px 12px 12px' }}>
                <button className="btn-blue-light fusa-btn-small" onClick={() => void navigate(`/projects/${project.id}/requirements`)}>
                  {I18n.get('viewAllRequirements')}
                </button>
              </div> }
            </div>
          )}
        </div>
        {userOrgId === project.propelOrgId && (
          <ProjectReadyTaskCard projectId={project.id} />
        )}
      </div>
    </div>
  );
}

function CreateProjectFromTemplateButton({ templateProject }: { templateProject: Project }) {
  const userClass = useGlobalStore(state => state.userClass);
  const [showModal, setShowModal] = useState(false);

  if (!userClass?.fusaHasPermission(FusawarePermission.EDIT_HIERARCHY)) {
    return null;
  }

  return (
    <>
      <Button className="btn btn-primary btn-sm mt-2" onClick={() => setShowModal(true)}>
        {I18n.get('createProjectFromTemplate')}
      </Button>
      {showModal && (
        <CreateProjectFromTemplateModal
          templateProjects={[templateProject]}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}
